import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaCloudDownloadAlt,
  FaCoins,
  FaColumns,
  FaCompressAlt,
  FaEllipsisH,
  FaExclamationTriangle,
  FaExpandAlt,
  FaFileExcel,
  FaFilter,
  FaPrint,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import Spinner from "../../components/Spinner";
import SchoolYearDropdown from "../../components/SchoolYearDropdown";
import ScrollJumpButtons from "../../components/ScrollJumpButtons";
import ErrorPage from "../../components/ErrorPage";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import ParentPortalVisibility from "./ParentPortalVisibility";
import EBoekhoudenStatusDot from "../../components/eboekhouden/EBoekhoudenStatusDot";
import { LdsTableCard } from "../../components/lds/LdsTable";
import http from "../../api/apiClient";
import { useCan } from "../../authz/useCan";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { formatDutchDate } from "../../utils/dutchDate";
import { printPage } from "../../utils/printPage";
import {
  childName,
  downloadContributionsExcel,
  euro,
  matchesContributionSearch,
  matchesReceiptSearch,
  monthLabel,
  monthsOfYears,
  termWindowYears,
  type ContributionOverview,
  type ContributionRow,
  type ImportResult,
  type Payer,
  type WaitingPayments,
} from "./contributionsModel";
import {
  buildBlocks,
  buildColumns,
  defaultColumnKeys,
  loadVisibleColumns,
  matchesSearch,
  parentSlotCount,
  resetVisibleColumns,
  saveVisibleColumns,
  type FinanceParent,
  type FinanceStudentReport,
  type FinanceStudentRow,
} from "./financeStudentsColumns";
import { downloadFinanceStudentsExcel } from "./financeStudentsExcelExport";
import { Legend } from "./ContributionDetails";
import { IgnoredList, WaitingList } from "./PaymentsInbox";
import { PayersSection } from "./PayersSection";
import FamilyView, { type FamilyGroup } from "./FamilyView";
import ChildView, { type DetailAccess } from "./ChildView";
import ClassView, { classGroupLabel, groupByClass } from "./ClassView";
import type { FamilyOption } from "./FamilyPicker";

type View = "family" | "child" | "class";

/** The view in the address bar: ?view=kind or ?view=klas; per family has no parameter. */
const VIEW_PARAM: Record<View, string | null> = { family: null, child: "kind", class: "klas" };

const isOpenIn = (row: ContributionRow, month: string) =>
  row.cells.some((cell) => cell.month === month && (cell.status === "OPEN" || cell.status === "PARTIAL"));

/**
 * Parent contributions: per family (default), per child or per class. On top the work queue for payments from e-Boekhouden.
 * Money needs finance.contributions, contact details need finance.reports.students; each part shows what the
 * viewer may see.
 */
export default function FinanceContributions(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canContrib = useCan("finance.contributions", "view");
  const canManage = useCan("finance.contributions", "manage");
  const canAdjust = useCan("finance.contributions.adjustments", "manage");
  const canReport = useCan("finance.reports.students", "view");
  const canReportExport = useCan("finance.reports.students", "export");
  const canFullIban = useCan("families.bankaccounts", "view");
  const canOpenChild = useCan("family.children", "read");
  const canOpenParent = useCan("family.parents", "read");
  const canOpenFamily = useCan("families", "read");
  const detailAccess = useMemo<DetailAccess>(() => ({
    child: canOpenChild,
    parent: canOpenParent || canOpenFamily,
    family: canOpenFamily,
  }), [canOpenChild, canOpenParent, canOpenFamily]);

  const [searchParams, setSearchParams] = useSearchParams();
  const schoolYearId = searchParams.get("schoolYear") || "";
  const requestedView: View = searchParams.get("view") === "kind" ? "child" : searchParams.get("view") === "klas" ? "class" : "family";
  // Per child shows contact details (finance.reports.students); per family and per class only need the contributions.
  const view: View = !canContrib ? "child" : requestedView === "child" && !canReport ? "family" : requestedView;
  const classFilter = searchParams.get("klas") || "";
  const setParam = (name: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(name, value); else next.delete(name);
    setSearchParams(next, { replace: true });
  };

  const [overview, setOverview] = useState<ContributionOverview | null>(null);
  const [report, setReport] = useState<FinanceStudentReport | null>(null);
  const [waiting, setWaiting] = useState<WaitingPayments | null>(null);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message?: string; status?: number } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => {
    setReloadKey((key) => key + 1);
    // The menu shows how many payments are still to link.
    window.dispatchEvent(new CustomEvent("lumen:finance-waiting-changed"));
  };

  const [search, setSearch] = useState("");
  const [excludeCurrentMonth, setExcludeCurrentMonth] = useState(false);
  const [onlyBehind, setOnlyBehind] = useState(false);
  const [openIn, setOpenIn] = useState("");
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [expandedChildren, setExpandedChildren] = useState<Set<string>>(new Set());
  const [visibleKeys, setVisibleKeys] = useState<string[] | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [includeFullIban, setIncludeFullIban] = useState(false);
  const [busy, setBusy] = useState<"import" | "export" | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const params = { ...(schoolYearId ? { schoolYearId } : {}), ...(excludeCurrentMonth ? { excludeCurrentMonth: true } : {}) };
    Promise.all([
      canContrib ? http.get("/finance/contributions", { params }) : Promise.resolve({ data: null }),
      canReport ? http.get("/finance/reports/students", { params }) : Promise.resolve({ data: null }),
      canContrib ? http.get("/finance/payments/waiting").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      canContrib ? http.get("/finance/payments/payers").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ])
      .then(([overviewResponse, reportResponse, waitingResponse, payersResponse]) => {
        if (cancelled) return;
        setOverview(overviewResponse.data && Array.isArray(overviewResponse.data.rows) ? { ...overviewResponse.data, excludeCurrentMonth } : null);
        setReport(reportResponse.data && Array.isArray(reportResponse.data.rows) ? reportResponse.data : null);
        setWaiting(waitingResponse.data && Array.isArray(waitingResponse.data.receipts) ? waitingResponse.data : null);
        setPayers(Array.isArray(payersResponse.data) ? payersResponse.data : []);
      })
      .catch((caught) => {
        if (!cancelled) setError(mapHttpErrorToMessage(caught, t));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t, schoolYearId, reloadKey, canContrib, canReport, excludeCurrentMonth]);

  // ---------- derived data ----------
  // Only the current school year, the one before and the one after are offered.
  const schoolYears = termWindowYears(overview?.schoolYears ?? report?.schoolYears ?? []);
  // A waiting payment may be for a month of the previous or next school year (paid in August for July).
  const termMonths = monthsOfYears(termWindowYears(overview?.schoolYears ?? []));
  const selectedYearId = overview?.schoolYear?.id ?? report?.schoolYear?.id ?? "";
  const monthlyFee = overview?.monthlyFee ?? report?.schoolYear?.monthlyFee ?? null;
  const contributionByChild = useMemo(() => new Map((overview?.rows ?? []).map((row) => [row.childId, row])), [overview]);

  const groups = useMemo<FamilyGroup[]>(() => {
    const byFamily = new Map<string, ContributionRow[]>();
    (overview?.rows ?? []).forEach((row) => {
      if (row.familyId) byFamily.set(row.familyId, [...(byFamily.get(row.familyId) ?? []), row]);
    });
    return (overview?.families ?? []).map((family) => {
      const children = byFamily.get(family.familyId) ?? [];
      return { family, children, expectedYear: children.reduce((sum, child) => sum + Number(child.expectedYear), 0) };
    });
  }, [overview]);

  const parentsByFamily = useMemo<Map<string, FinanceParent[]> | null>(() => {
    if (!report) return null;
    const result = new Map<string, FinanceParent[]>();
    report.rows.forEach((row) => (row.families ?? []).forEach((family) => {
      const list = result.get(family.familyId) ?? [];
      (row.parents ?? []).forEach((parent) => {
        if (!list.some((known) => known.parentId === parent.parentId)) list.push(parent);
      });
      result.set(family.familyId, list);
    }));
    return result;
  }, [report]);

  const familyOptions = useMemo<FamilyOption[]>(() => groups.map((group) => ({
    familyId: group.family.familyId,
    familyName: group.family.familyName,
    childNames: group.children.map((child) => [child.firstName, child.lastName].filter(Boolean).join(" ")),
    children: group.children.map((child) => ({ childId: child.childId, name: [child.firstName, child.lastName].filter(Boolean).join(" ") })),
  })), [groups]);
  const familyNames = useMemo(() => new Map(groups.map((group) => [group.family.familyId, group.family.familyName || "—"])), [groups]);

  const moneyFilter = (row?: ContributionRow) =>
    (!onlyBehind || (row !== undefined && Number(row.balance) < 0)) && (!openIn || (row !== undefined && isOpenIn(row, openIn)));

  const visibleGroups = useMemo(() => groups.filter((group) => {
    if (onlyBehind && Number(group.family.balance) >= 0) return false;
    if (openIn && !group.children.some((child) => isOpenIn(child, openIn))) return false;
    const query = search.trim().toLowerCase();
    if (!query) return true;
    const parents = parentsByFamily?.get(group.family.familyId) ?? [];
    return String(group.family.familyName ?? "").toLowerCase().includes(query)
      || group.children.some((child) => matchesContributionSearch(child, query))
      || parents.some((parent) => [parent.firstName, parent.lastName, parent.email].filter(Boolean).join(" ").toLowerCase().includes(query));
  }), [groups, onlyBehind, openIn, search, parentsByFamily]);

  const classGroups = useMemo(() => groupByClass(overview?.rows ?? []), [overview]);
  const visibleClassGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return classGroups
      .filter((group) => !classFilter || group.key === classFilter)
      .map((group) => ({
        ...group,
        children: group.children.filter((child) => moneyFilter(child)
          && (!query || matchesContributionSearch(child, query)
            || classGroupLabel(t, group).toLowerCase().includes(query)
            || group.teachers.some((teacher) => teacher.toLowerCase().includes(query)))),
      }))
      .filter((group) => group.children.length > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classGroups, classFilter, search, onlyBehind, openIn, t]);
  const visibleClassChildren = useMemo(() => visibleClassGroups.flatMap((group) => group.children), [visibleClassGroups]);

  const reportRows = useMemo(() => report?.rows ?? [], [report]);
  const visibleChildren = useMemo(() => reportRows
    .filter((row) => matchesSearch(row, search))
    .filter((row) => (!onlyBehind && !openIn) || moneyFilter(contributionByChild.get(row.childId))),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [reportRows, search, onlyBehind, openIn, contributionByChild]);

  const columns = useMemo(() => buildColumns(t, parentSlotCount(reportRows)), [t, reportRows]);
  useEffect(() => {
    if (report && visibleKeys === null) setVisibleKeys(loadVisibleColumns(columns));
  }, [report, columns, visibleKeys]);
  const visibleColumns = useMemo(() => {
    const visible = new Set(visibleKeys ?? defaultColumnKeys(columns));
    return columns.filter((column) => visible.has(column.key));
  }, [columns, visibleKeys]);
  const blocks = useMemo(() => buildBlocks(visibleColumns), [visibleColumns]);
  const ibanColumnVisible = visibleColumns.some((column) => column.kind === "iban");

  const toggleColumn = (key: string) => {
    const current = visibleKeys ?? defaultColumnKeys(columns);
    const next = current.includes(key) ? current.filter((value) => value !== key) : [...current, key];
    if (next.length === 0) return;
    setVisibleKeys(next);
    saveVisibleColumns(next);
  };

  const waitingOpen = (waiting?.receipts ?? []).filter((receipt) => !receipt.ignored);
  const waitingIgnored = (waiting?.receipts ?? []).filter((receipt) => receipt.ignored);
  // The search box also looks through the payments still to link (payer, description, IBAN, amount, date).
  const searching = search.trim().length > 0;
  const waitingShown = useMemo(() => {
    const toLink = (waiting?.receipts ?? []).filter((receipt) => !receipt.ignored);
    return searching ? toLink.filter((receipt) => matchesReceiptSearch(receipt, search)) : toLink;
  }, [waiting, search, searching]);
  const ignoredShown = searching ? waitingIgnored.filter((receipt) => matchesReceiptSearch(receipt, search)) : waitingIgnored;
  const childrenWithoutFamily = (overview?.rows ?? []).filter((row) => !row.familyId);
  const familyTotals = groups.reduce((sum, group) => ({
    expected: sum.expected + Number(group.family.expectedToDate),
    received: sum.received + Number(group.family.received),
    behind: sum.behind + (Number(group.family.balance) < 0 ? 1 : 0),
  }), { expected: 0, received: 0, behind: 0 });
  const activeCount = reportRows.filter((row) => row.status === "ACTIVE").length;

  const expandedSet = view === "family" ? expandedFamilies : expandedChildren;
  const visibleIds = view === "family" ? visibleGroups.map((group) => group.family.familyId)
    : view === "class" ? visibleClassChildren.map((row) => row.childId)
      : visibleChildren.map((row) => row.childId);
  const allExpanded = visibleIds.length > 0 && visibleIds.every((id) => expandedSet.has(id));
  const toggleIn = (setter: typeof setExpandedFamilies) => (id: string) => setter((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const filterCount = (excludeCurrentMonth ? 1 : 0) + (onlyBehind ? 1 : 0) + (openIn ? 1 : 0);

  // ---------- actions ----------
  const importErrorText = (code?: string) => {
    switch (code) {
      case "EBOEKHOUDEN_NOT_CONFIGURED":
        return t("finance.payments.import_errors.not_configured", { defaultValue: "The connection with e-Boekhouden is not set up yet. See Finance › Settings." });
      case "EBOEKHOUDEN_DISABLED":
        return t("finance.payments.import_errors.disabled", { defaultValue: "The connection with e-Boekhouden is switched off. See Finance › Settings." });
      case "EBOEKHOUDEN_UNREACHABLE":
        return t("finance.payments.import_errors.unreachable", { defaultValue: "e-Boekhouden could not be reached. Try again later." });
      case "EBOEKHOUDEN_REJECTED":
        return t("finance.payments.import_errors.rejected", { defaultValue: "e-Boekhouden refused the request. Check the token in the settings." });
      default:
        return t("finance.payments.import_errors.error", { defaultValue: "Reading payments failed." });
    }
  };

  const handleImport = async () => {
    setBusy("import");
    setNotice(null);
    setImportResult(null);
    try {
      const { data } = await http.post("/finance/payments/import", null, { params: selectedYearId ? { schoolYearId: selectedYearId } : {} });
      setImportResult(data);
      reload();
    } catch (caught: any) {
      setNotice(importErrorText(caught?.response?.data?.message));
    } finally {
      setBusy(null);
    }
  };

  const exportAmounts = async () => {
    if (!overview) return;
    setBusy("export");
    setNotice(null);
    try {
      const { data } = await http.post("/finance/contributions/export", null, { params: { ...(selectedYearId ? { schoolYearId: selectedYearId } : {}), ...(excludeCurrentMonth ? { excludeCurrentMonth: true } : {}) } });
      const filtered = search.trim() || onlyBehind || openIn;
      const allowedFamilies = new Set(visibleGroups.map((group) => group.family.familyId));
      const allowedChildren = new Set((view === "class" ? visibleClassChildren : visibleChildren).map((row) => row.childId));
      const rows = (data?.rows ?? []).filter((row: ContributionRow) => !filtered
        || (view === "family" ? Boolean(row.familyId && allowedFamilies.has(row.familyId)) : allowedChildren.has(row.childId)));
      await downloadContributionsExcel(data, rows, t);
    } catch (caught) {
      setNotice(mapHttpErrorToMessage(caught, t)?.message || t("finance.students_report.export_failed", { defaultValue: "The export failed. Please try again." }));
    } finally {
      setBusy(null);
    }
  };

  const exportParents = async () => {
    setBusy("export");
    setNotice(null);
    const fullIban = canFullIban && ibanColumnVisible && includeFullIban;
    try {
      const { data } = await http.post("/finance/reports/students/export", {
        includeFullIban: fullIban,
        columns: visibleColumns.map((column) => column.key),
        search: search.trim() || null,
        schoolYearId: selectedYearId || null,
      });
      const exportRows: FinanceStudentRow[] = Array.isArray(data?.rows) ? data.rows : [];
      const allowed = new Set(visibleChildren.map((row) => row.childId));
      const exportColumns = buildColumns(t, parentSlotCount(exportRows))
        .filter((column) => visibleColumns.some((visible) => visible.key === column.key));
      await downloadFinanceStudentsExcel(exportColumns, exportRows.filter((row) => allowed.has(row.childId)),
        t("finance.students_report.sheet_name", { defaultValue: "Children and parents" }));
    } catch (caught) {
      setNotice(mapHttpErrorToMessage(caught, t)?.message || t("finance.students_report.export_failed", { defaultValue: "The export failed. Please try again." }));
    } finally {
      setBusy(null);
    }
  };

  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;

  const control = "h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-white/10 dark:bg-slate-900 dark:text-white";
  const iconButton = "inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:border-indigo-200 disabled:opacity-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200";
  const showInbox = canContrib && (waitingOpen.length > 0 || waitingIgnored.length > 0 || importResult !== null);

  return (
    <LdsOverviewPage className="finance-students-page">
      <ScrollJumpButtons />
      {view === "child" && (
        <div className="finance-students-print-only">
          <h1 className="text-base font-bold">{t("finance.contributions.title", { defaultValue: "Parent contributions" })}</h1>
          <p className="text-xs">
            {formatDutchDate(new Date())} · {t("finance.students_report.counts", {
              active: activeCount,
              withdrawn: reportRows.length - activeCount,
              shown: visibleChildren.length,
              defaultValue: "{{active}} active, {{withdrawn}} withdrawn · {{shown}} shown",
            })}
          </p>
        </div>
      )}

      <div className="finance-students-no-print">
        <LdsOverviewHero
          icon={FaCoins}
          eyebrow={t("finance.overview.title", { defaultValue: "Finance" })}
          title={t("finance.contributions.title", { defaultValue: "Parent contributions" })}
          description={t("finance.contributions.description_combined", {
            defaultValue: "What each family and child had to pay this school year (1 August – 31 July) and what came in, with the parents' details.",
          })}
          actions={canContrib ? <EBoekhoudenStatusDot /> : undefined}
        />
      </div>

      {canContrib && <ParentPortalVisibility canManage={canManage} />}

      <section className="finance-students-no-print sticky top-3 z-30 rounded-[24px] border border-slate-200/80 bg-white/95 p-3 shadow-lg backdrop-blur-md dark:border-slate-800 dark:bg-slate-950/95">
        <div className="flex flex-wrap items-end gap-2">
          <SchoolYearDropdown
            schoolYears={schoolYears}
            selectedSchoolYearId={selectedYearId}
            onChange={(id) => {
              setParam("schoolYear", id);
              setExpandedFamilies(new Set());
              setExpandedChildren(new Set());
            }}
            label={t("finance.contributions.school_year", { defaultValue: "School year" })}
          />
          <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t("finance.contributions.search_all", { defaultValue: "Search child, family or payment" })}
              placeholder={t("finance.contributions.search_all", { defaultValue: "Search child, family or payment" })}
              className={`${control} w-full pl-9`}
            />
          </div>
          {canContrib && (
            <Popover
              label={t("common.filter", { defaultValue: "Filter" })}
              button={(
                <span className="inline-flex items-center gap-2">
                  <FaFilter aria-hidden="true" />
                  {t("common.filter", { defaultValue: "Filter" })}
                  {filterCount > 0 && (
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[11px] text-white">{filterCount}</span>
                  )}
                </span>
              )}
              buttonClassName={`inline-flex h-10 items-center rounded-xl border px-3 text-sm font-semibold ${filterCount > 0
                ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-200"
                : "border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"}`}
            >
              <div className="grid w-64 gap-3 p-3 text-sm">
                <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <input type="checkbox" checked={excludeCurrentMonth} onChange={(event) => {
                    setExcludeCurrentMonth(event.target.checked);
                    if (event.target.checked && openIn === overview?.currentMonth) setOpenIn("");
                  }} />
                  {t("finance.contributions.exclude_current_month", { defaultValue: "Exclude current month" })}
                </label>
                <label className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <input type="checkbox" checked={onlyBehind} onChange={(event) => setOnlyBehind(event.target.checked)} />
                  {t("finance.contributions.only_behind", { defaultValue: "Only behind" })}
                </label>
                <label className="grid gap-1 text-xs font-bold text-slate-600 dark:text-slate-300">
                  {t("finance.contributions.open_in", { defaultValue: "Open in" })}
                  <select value={openIn} onChange={(event) => setOpenIn(event.target.value)} className={control}>
                    <option value="">{t("finance.contributions.open_in_any", { defaultValue: "any month" })}</option>
                    {(overview?.months ?? []).filter((month) => !overview?.currentMonth || (excludeCurrentMonth ? month < overview.currentMonth : month <= overview.currentMonth)).map((month) => (
                      <option key={month} value={month}>{monthLabel(month, "long")}</option>
                    ))}
                  </select>
                </label>
                {filterCount > 0 && (
                  <button type="button" onClick={() => { setOnlyBehind(false); setOpenIn(""); setExcludeCurrentMonth(false); }}
                    className="inline-flex items-center gap-1 justify-self-start text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-300">
                    <FaTimes aria-hidden="true" />
                    {t("finance.contributions.clear_filters", { defaultValue: "Clear filters" })}
                  </button>
                )}
              </div>
            </Popover>
          )}

          <div className="ml-auto flex items-center gap-2">
            {view === "class" && classGroups.length > 0 && (
              <select
                value={classFilter}
                onChange={(event) => setParam("klas", event.target.value || null)}
                aria-label={t("finance.contributions.class_filter", { defaultValue: "Class" })}
                className={control}
              >
                <option value="">{t("finance.contributions.all_classes", { defaultValue: "All classes" })}</option>
                {classGroups.map((group) => (
                  <option key={group.key} value={group.key}>
                    {classGroupLabel(t, group)}{group.teachers.length > 0 ? ` – ${group.teachers.join(", ")}` : ""}
                  </option>
                ))}
              </select>
            )}
            {canContrib && (
              <div role="group" aria-label={t("finance.contributions.view", { defaultValue: "View" })}
                className="inline-flex h-10 rounded-xl border border-slate-200 bg-white p-0.5 text-sm font-semibold dark:border-white/10 dark:bg-slate-900">
                {((canReport ? ["family", "child", "class"] : ["family", "class"]) as View[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={view === option}
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      const value = VIEW_PARAM[option];
                      if (value) next.set("view", value); else next.delete("view");
                      if (option !== "class") next.delete("klas");
                      setSearchParams(next, { replace: true });
                    }}
                    className={`rounded-lg px-3 ${view === option ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5"}`}
                  >
                    {option === "family"
                      ? t("finance.contributions.per_family", { defaultValue: "Per family" })
                      : option === "child"
                        ? t("finance.contributions.per_child", { defaultValue: "Per child" })
                        : t("finance.contributions.per_class", { defaultValue: "Per class" })}
                  </button>
                ))}
              </div>
            )}
            {(view === "family" || canContrib) && (
              <button
                type="button"
                onClick={() => (view === "family" ? setExpandedFamilies : setExpandedChildren)(allExpanded ? new Set() : new Set(visibleIds))}
                disabled={loading || visibleIds.length === 0}
                aria-label={allExpanded
                  ? t("finance.contributions.collapse_all", { defaultValue: "Collapse all" })
                  : t("finance.contributions.expand_all", { defaultValue: "Expand all" })}
                title={allExpanded
                  ? t("finance.contributions.collapse_all", { defaultValue: "Collapse all" })
                  : t("finance.contributions.expand_all", { defaultValue: "Expand all" })}
                className={iconButton}
              >
                {allExpanded ? <FaCompressAlt aria-hidden="true" /> : <FaExpandAlt aria-hidden="true" />}
              </button>
            )}
            <Popover
              label={t("finance.contributions.more_actions", { defaultValue: "More actions" })}
              button={<FaEllipsisH aria-hidden="true" />}
              buttonClassName={iconButton}
              align="right"
            >
              <div role="menu" className="flex w-72 flex-col py-1 text-sm">
                {canManage && (
                  <MenuItem icon={<FaCloudDownloadAlt aria-hidden="true" />} onSelect={handleImport} disabled={busy !== null}
                    hint={waiting?.lastImportAt
                      ? t("finance.payments.last_import_short", { date: formatDutchDate(waiting.lastImportAt), defaultValue: "last on {{date}}" })
                      : undefined}>
                    {busy === "import"
                      ? t("finance.payments.importing", { defaultValue: "Reading…" })
                      : t("finance.payments.import", { defaultValue: "Read from e-Boekhouden" })}
                  </MenuItem>
                )}
                {canContrib && (
                  <MenuItem icon={<FaFileExcel aria-hidden="true" />} onSelect={exportAmounts} disabled={busy !== null || !overview}>
                    {t("finance.contributions.export_amounts", { defaultValue: "Export amounts per month (Excel)" })}
                  </MenuItem>
                )}
                {canReportExport && (
                  <MenuItem icon={<FaFileExcel aria-hidden="true" />} onSelect={exportParents} disabled={busy !== null || !report}>
                    {t("finance.contributions.export_parents", { defaultValue: "Export children and parents (Excel)" })}
                  </MenuItem>
                )}
                {canReportExport && canFullIban && ibanColumnVisible && (
                  <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <input type="checkbox" checked={includeFullIban} onChange={(event) => setIncludeFullIban(event.target.checked)} />
                    {t("finance.students_report.full_iban_toggle", { defaultValue: "Full IBAN in export (logged)" })}
                  </label>
                )}
                {view === "child" && (
                  <>
                    <MenuItem icon={<FaColumns aria-hidden="true" />} onSelect={() => setColumnsOpen((value) => !value)}>
                      {t("finance.students_report.columns_button", { defaultValue: "Columns" })}
                    </MenuItem>
                    <MenuItem icon={<FaPrint aria-hidden="true" />} onSelect={() => printPage()} disabled={visibleChildren.length === 0}>
                      {t("finance.students_report.print", { defaultValue: "Print" })}
                    </MenuItem>
                  </>
                )}
              </div>
            </Popover>
          </div>
        </div>

        {view === "child" && columnsOpen && (
          <ColumnPicker
            columns={columns}
            visibleKeys={visibleColumns.map((column) => column.key)}
            onToggle={toggleColumn}
            onReset={() => {
              resetVisibleColumns();
              setVisibleKeys(defaultColumnKeys(columns));
            }}
            onClose={() => setColumnsOpen(false)}
          />
        )}
        {notice && <p role="alert" className="mt-2 text-sm font-semibold text-red-600">{notice}</p>}
      </section>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {showInbox && (
            <div className="finance-students-no-print grid gap-3">
              {importResult && (
                <p role="status" className="rounded-xl bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 dark:bg-emerald-400/10 dark:text-emerald-200">
                  {t("finance.payments.import_result", {
                    imported: importResult.imported,
                    iban: importResult.linkedByIban,
                    relation: importResult.linkedByRelation,
                    waiting: importResult.waiting,
                    defaultValue: "{{imported}} new payment(s): {{iban}} linked by IBAN, {{relation}} by an earlier link, {{waiting}} to link.",
                  })}
                  {(importResult.linkedLater ?? 0) > 0 && (
                    <span className="ml-1">
                      {t("finance.payments.import_linked_later", {
                        count: importResult.linkedLater,
                        defaultValue: "{{count}} payment(s) that were waiting are now linked by IBAN.",
                      })}
                    </span>
                  )}
                  <span className="ml-1 font-normal opacity-80">
                    {t("finance.payments.import_period", {
                      from: formatDutchDate(importResult.from),
                      to: formatDutchDate(importResult.to),
                      already: importResult.alreadyImported,
                      defaultValue: "({{from}} to {{to}}; {{already}} already read)",
                    })}
                  </span>
                </p>
              )}
              {waitingOpen.length > 0 && (
                <WaitingList receipts={waitingShown} totalCount={waitingOpen.length} searching={searching}
                  families={familyOptions} suggestions={waiting?.suggestions ?? {}} canManage={canManage} onChanged={reload}
                  months={termMonths} />
              )}
              {ignoredShown.length > 0 && <IgnoredList receipts={ignoredShown} canManage={canManage} onChanged={reload} />}
            </div>
          )}

          <LdsTableCard className="finance-students-card overflow-hidden">
            <div className="finance-students-no-print flex flex-col gap-1 border-b border-slate-200 px-5 py-3 text-sm dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-slate-500 dark:text-slate-400">
                {view === "family"
                  ? t("finance.contributions.families_summary", { families: groups.length, behind: familyTotals.behind, defaultValue: "{{families}} families · {{behind}} behind" })
                  : view === "class"
                    ? t("finance.contributions.classes_summary", {
                      classes: visibleClassGroups.length,
                      children: visibleClassChildren.length,
                      defaultValue: "{{classes}} class(es) · {{children}} child(ren)",
                    })
                    : t("finance.students_report.counts", {
                    active: activeCount,
                    withdrawn: reportRows.length - activeCount,
                    shown: visibleChildren.length,
                    defaultValue: "{{active}} active, {{withdrawn}} withdrawn · {{shown}} shown",
                  })}
              </p>
              <p className="font-semibold text-slate-700 dark:text-slate-200">
                {monthlyFee == null
                  ? t("finance.students_report.no_fee", { defaultValue: "No contribution set for the current school year" })
                  : canContrib
                    ? t("finance.contributions.totals_received", {
                      fee: euro(monthlyFee),
                      expected: euro(familyTotals.expected),
                      received: euro(familyTotals.received),
                      defaultValue: "{{fee}} per child per month · expected to date {{expected}} · received {{received}}",
                    })
                    : t("finance.students_report.monthly_fee", { fee: euro(monthlyFee), defaultValue: "Contribution per child: {{fee}} per month" })}
              </p>
            </div>
            {view === "family" && overview ? (
              <FamilyView
                groups={visibleGroups}
                overview={overview}
                parentsByFamily={parentsByFamily}
                detailAccess={detailAccess}
                canManage={canManage}
                canAdjust={canAdjust}
                expanded={expandedFamilies}
                onToggle={toggleIn(setExpandedFamilies)}
                onChanged={reload}
                familyOptions={familyOptions}
                payers={payers}
              />
            ) : view === "class" && overview ? (
              <ClassView
                groups={visibleClassGroups}
                overview={overview}
                detailAccess={detailAccess}
                canManage={canManage}
                expanded={expandedChildren}
                onToggle={toggleIn(setExpandedChildren)}
                onChanged={reload}
                familyOptions={familyOptions}
              />
            ) : (
              <ChildView
                rows={visibleChildren}
                blocks={blocks}
                detailAccess={detailAccess}
                contributions={canContrib ? overview : null}
                canManage={canManage}
                expanded={expandedChildren}
                onToggle={toggleIn(setExpandedChildren)}
                onOpenChild={canOpenChild ? (childId) => navigate(`/family/children/${childId}/details`) : undefined}
                onChanged={reload}
                familyOptions={familyOptions}
              />
            )}
            {view === "family" && childrenWithoutFamily.length > 0 && (
              <p className="flex items-start gap-2 border-t border-slate-200 px-5 py-3 text-xs text-amber-700 dark:border-white/10 dark:text-amber-300">
                <FaExclamationTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  {t("finance.contributions.children_without_family", {
                    count: childrenWithoutFamily.length,
                    names: childrenWithoutFamily.map(childName).join(", "),
                    defaultValue: "{{count}} child(ren) without family, so no payments can be linked: {{names}}",
                  })}
                </span>
              </p>
            )}
            {canContrib && <div className="finance-students-no-print"><Legend /></div>}
          </LdsTableCard>

          {canContrib && payers.length > 0 && (
            <div className="finance-students-no-print">
              <PayersSection payers={payers} familyNames={familyNames} canManage={canManage} onChanged={reload} />
            </div>
          )}
        </>
      )}
    </LdsOverviewPage>
  );
}

/** A button with a small panel underneath that closes on a click outside it. */
function Popover({ label, button, buttonClassName, align = "left", children }: {
  label: string;
  button: ReactNode;
  buttonClassName: string;
  align?: "left" | "right";
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-label={label} title={label} aria-expanded={open} onClick={() => setOpen((value) => !value)} className={buttonClassName}>
        {button}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={label}
          onClick={(event) => {
            // Menu items close the panel; form controls keep it open.
            if ((event.target as HTMLElement).closest("[role=menuitem]")) setOpen(false);
          }}
          className={`absolute z-[70] mt-2 rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-slate-900 ${align === "right" ? "right-0" : "left-0"}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, children, hint, onSelect, disabled }: { icon: ReactNode; children: ReactNode; hint?: string; onSelect: () => void; disabled?: boolean }) {
  return (
    <button type="button" role="menuitem" onClick={onSelect} disabled={disabled}
      className="flex items-start gap-2 px-3 py-2 text-left text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-white/5">
      <span className="mt-0.5 text-slate-500">{icon}</span>
      <span>
        {children}
        {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
      </span>
    </button>
  );
}

function ColumnPicker({ columns, visibleKeys, onToggle, onReset, onClose }: {
  columns: ReturnType<typeof buildColumns>;
  visibleKeys: string[];
  onToggle: (key: string) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const groups = Array.from(new Set(columns.map((column) => column.group)));
  return (
    <div className="mt-3 border-t border-slate-200 pt-3 dark:border-white/10" role="group"
      aria-label={t("finance.students_report.columns_button", { defaultValue: "Columns" })}>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {groups.map((group) => (
          <fieldset key={group}>
            <legend className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{group}</legend>
            {columns.filter((column) => column.group === group).map((column) => (
              <label key={column.key} className="flex items-center gap-2 py-0.5 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={visibleKeys.includes(column.key)} onChange={() => onToggle(column.key)} />
                {column.label.replace(`${group} – `, "")}
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      <div className="mt-2 flex gap-4">
        <button type="button" onClick={onReset} className="text-sm font-bold text-indigo-600 hover:underline dark:text-indigo-300">
          {t("finance.students_report.reset_columns", { defaultValue: "Restore default columns" })}
        </button>
        <button type="button" onClick={onClose} className="text-sm font-semibold text-slate-500 hover:underline">
          {t("common.close", { defaultValue: "Close" })}
        </button>
      </div>
    </div>
  );
}
